export type TenantPathSegmentParse =
    | { kind: 'none' }
    | { kind: 'error'; error: string }
    | { kind: 'ok'; displayId: string; instance: string }

/**
 * Decode the first `/_…` segment: optional `-` splits **tenant display id** (left) from **instance**
 * (right, digits only). Extra hyphens stay in the instance part and fail validation.
 */
export function parseTenantPathSegment(pathname: string): TenantPathSegmentParse {
    const segments = pathname.split('/').filter(Boolean)
    const first = segments[0]
    if (!first?.startsWith('_') || first.length <= 1) {
        return { kind: 'none' }
    }
    let raw = first.slice(1)
    try {
        raw = decodeURIComponent(raw)
    } catch {
        /* keep undecoded slice */
    }
    const hyphen = raw.indexOf('-')
    if (hyphen < 0) {
        return { kind: 'ok', displayId: raw, instance: '' }
    }
    const displayId = raw.slice(0, hyphen)
    const instancePart = raw.slice(hyphen + 1)
    if (!/^\d+$/.test(instancePart)) {
        return { kind: 'error', error: 'invalid instance number' }
    }
    return { kind: 'ok', displayId, instance: instancePart }
}

/** First path segment `_…` forms the tenancy URL prefix `/_slug` used as the router basename. */
export function getTenantPathPrefix(pathname: string): string | null {
    const segments = pathname.split('/').filter(Boolean)
    const first = segments[0]
    if (first?.startsWith('_') && first.length > 1) {
        return '/' + first
    }
    return null
}

/** Payload for POST /tenants/lookup-by-display-id (decoded display id only; instance lives in the URL after `-`). */
export function displayIdFromTenantPath(pathname: string): string | null {
    const parsed = parseTenantPathSegment(pathname)
    if (parsed.kind !== 'ok') return null
    return parsed.displayId
}

/** Organisation-picker routes (no `/_organisation` segment). */
export function isOrgPickerPath(pathname: string): boolean {
    const p = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname
    return p === '/' || p === '/switch'
}
